
import { SystemConfig, SofiaVoiceConfig } from '../types';
import { toast } from 'react-hot-toast';

// --- Web Audio API for simple sounds ---
let audioCtx: AudioContext | null = null;
const getAudioContext = () => {
    if (!audioCtx) {
        audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    return audioCtx;
};

export const playSound = (type: 'success' | 'alert' | 'delivery' | 'error') => {
    try {
        const ctx = getAudioContext();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.connect(gain);
        gain.connect(ctx.destination);

        const now = ctx.currentTime;

        if (type === 'success') {
            osc.type = 'sine';
            osc.frequency.setValueAtTime(523.25, now);
            osc.frequency.exponentialRampToValueAtTime(659.25, now + 0.1);
            gain.gain.setValueAtTime(0.3, now);
            gain.gain.exponentialRampToValueAtTime(0.01, now + 0.5);
            osc.start(now);
            osc.stop(now + 0.5);
        }
        else if (type === 'alert') {
            osc.type = 'sawtooth';
            osc.frequency.setValueAtTime(150, now);
            osc.frequency.linearRampToValueAtTime(100, now + 0.2);
            gain.gain.setValueAtTime(0.2, now);
            gain.gain.linearRampToValueAtTime(0.01, now + 0.3);
            osc.start(now);
            osc.stop(now + 0.3);
        }
        else if (type === 'delivery') {
            osc.type = 'triangle';
            osc.frequency.setValueAtTime(440, now);
            osc.frequency.setValueAtTime(554.37, now + 0.1);
            osc.frequency.setValueAtTime(659.25, now + 0.2);
            gain.gain.setValueAtTime(0.3, now);
            gain.gain.exponentialRampToValueAtTime(0.01, now + 0.6);
            osc.start(now);
            osc.stop(now + 0.6);
        }
    } catch (e) {
        console.error("Audio Playback Failed", e);
    }
};

// --- Web Speech API for Local TTS ---
let localSpeechSynth: SpeechSynthesis | null = null;
let localVoices: SpeechSynthesisVoice[] = [];

const getLocalSpeechSynth = () => {
    if (!localSpeechSynth) {
        localSpeechSynth = window.speechSynthesis;
        // Ensure voices are loaded
        if (localSpeechSynth.getVoices().length === 0) {
            localSpeechSynth.onvoiceschanged = () => {
                localVoices = localSpeechSynth!.getVoices();
                localSpeechSynth!.onvoiceschanged = null; // Remove listener after loading
            };
        } else {
            localVoices = localSpeechSynth.getVoices();
        }
    }
    return localSpeechSynth;
};

export const getAvailableLocalVoices = (): SpeechSynthesisVoice[] => {
    if (localVoices.length === 0) {
        getLocalSpeechSynth(); // Trigger loading if not already
    }
    return localVoices.filter(voice => voice.lang.startsWith('pt-'));
};

// --- Simulated Google Cloud TTS API (for Neural2 voices) ---
// In a real application, this would involve a server-side call to Google Cloud TTS API.
// For this simulation, we'll hardcode some pt-BR Neural2-like voices.
export const getAvailableCloudVoices = () => [
    { name: 'pt-BR-Neural2-A (Feminino)', lang: 'pt-BR', default: false, localService: false, voiceURI: 'pt-BR-Neural2-A' },
    { name: 'pt-BR-Neural2-B (Masculino)', lang: 'pt-BR', default: false, localService: false, voiceURI: 'pt-BR-Neural2-B' },
    { name: 'pt-BR-Neural2-C (Feminino)', lang: 'pt-BR', default: false, localService: false, voiceURI: 'pt-BR-Neural2-C' },
    { name: 'pt-BR-Neural2-D (Masculino)', lang: 'pt-BR', default: false, localService: false, voiceURI: 'pt-BR-Neural2-D' },
    { name: 'pt-BR-Neural2-E (Feminino)', lang: 'pt-BR', default: false, localService: false, voiceURI: 'pt-BR-Neural2-E' },
];

// --- Main TTS Speak Function ---
interface SpeakOptions {
    text: string;
    systemConfig: SystemConfig;
    setSystemConfig: React.Dispatch<React.SetStateAction<SystemConfig>>;
    forceLocal?: boolean; // For explicit local fallback (e.g., Budget Guard)
}

// Function to simulate charging characters to the TTS budget
const chargeTtsCharacters = (charCount: number, currentConfig: SystemConfig, setSystemConfig: React.Dispatch<React.SetStateAction<SystemConfig>>) => {
    const now = Date.now();
    const lastReset = currentConfig.lastTtsUsageResetDate || 0;
    const maxFree = currentConfig.maxFreeTtsCharacters || 300000; // Default 300k chars for premium

    let charactersUsed = currentConfig.simulatedTtsCharactersUsed || 0;

    // Check for monthly reset (simple logic: reset if a new month has started since last reset)
    const lastResetDate = new Date(lastReset);
    const currentDate = new Date(now);

    if (lastReset === 0 || currentDate.getMonth() !== lastResetDate.getMonth() || currentDate.getFullYear() !== lastResetDate.getFullYear()) {
        charactersUsed = 0; // Reset for a new month
        toast.success("Limite de caracteres TTS reiniciado para o novo mês!", { icon: '📈' });
    }

    charactersUsed += charCount;

    setSystemConfig(prevConfig => ({
        ...prevConfig,
        simulatedTtsCharactersUsed: charactersUsed,
        lastTtsUsageResetDate: lastResetDate.getMonth() !== currentDate.getMonth() ? new Date(now).toISOString() : prevConfig.lastTtsUsageResetDate, // Update reset date only if reset happened
        maxFreeTtsCharacters: maxFree, // Ensure default is set if not already
    }));

    return charactersUsed;
};

// Simple summarization for objective interactions
const summarizeText = (text: string, maxLength: number = 100): string => {
    if (text.length <= maxLength) return text;

    // Try to find a natural break (e.g., end of a sentence)
    const shortText = text.substring(0, maxLength);
    const lastPeriod = shortText.lastIndexOf('.');
    const lastExclamation = shortText.lastIndexOf('!');
    const lastQuestion = shortText.lastIndexOf('?');
    const lastSentenceEnd = Math.max(lastPeriod, lastExclamation, lastQuestion);

    if (lastSentenceEnd > maxLength * 0.7) { // If a sentence ends within 70-100% of max length
        return shortText.substring(0, lastSentenceEnd + 1) + "...";
    }

    // Fallback to word boundary
    const lastSpace = shortText.lastIndexOf(' ');
    if (lastSpace > maxLength * 0.5) { // If a word boundary exists in latter half
        return shortText.substring(0, lastSpace) + "...";
    }

    return shortText + "..."; // Just truncate
};


export const speak = ({ text, systemConfig, setSystemConfig, forceLocal = false }: SpeakOptions) => {
    const effectiveSofiaConfig = systemConfig.communication?.sofiaVoiceConfig;

    if (!effectiveSofiaConfig) {
        console.warn("Sofia Voice Config not found.");
        return;
    }

    // Determine effective voice quality based on settings and budget
    let actualVoiceQuality = effectiveSofiaConfig.selectedVoiceQuality;
    let isBudgetFallbackActive = false;

    const maxFree = systemConfig.maxFreeTtsCharacters || 300000;
    const charactersUsed = systemConfig.simulatedTtsCharactersUsed || 0;
    const usagePercentage = (charactersUsed / maxFree) * 100;

    // Budget Guard override
    if (systemConfig.isEconomicMode && effectiveSofiaConfig.budgetGuardVoiceFallback) {
        actualVoiceQuality = 'local';
        isBudgetFallbackActive = true;
    }

    // Automatic fallback if 95% of free tier is used AND not already in local mode
    if (!isBudgetFallbackActive && actualVoiceQuality === 'cloud' && usagePercentage >= 95) {
        actualVoiceQuality = 'local';
        isBudgetFallbackActive = true;
        toast("Limite de voz Cloud quase atingido! Mudando para voz econômica local.", { duration: 5000 });
    }

    if (forceLocal) {
        actualVoiceQuality = 'local';
        // No toast here as forceLocal implies user/system intent (e.g. specific low-cost interactions)
    }

    const processedText = summarizeText(text, 150); // Keep interactions objective, max 150 chars

    if (actualVoiceQuality === 'local') {
        const synth = getLocalSpeechSynth();
        if (!synth) {
            console.error("SpeechSynthesis API not available.");
            return;
        }

        const utterance = new SpeechSynthesisUtterance(processedText);
        const ptVoices = getAvailableLocalVoices();
        if (ptVoices.length > 0) {
            // Try to find a suitable Portuguese voice, otherwise use default
            utterance.voice = ptVoices.find(voice => voice.name.includes('Google português do Brasil') || voice.name.includes('Portuguese Brazil')) || ptVoices[0];
        }
        utterance.lang = 'pt-BR';
        utterance.rate = 1.1; // Slightly faster for efficiency
        utterance.pitch = 1;

        synth.speak(utterance);
        // No character charge for local voices

    } else { // Cloud voice
        // In production, would make an actual API call via serverless function

        const finalCharCount = processedText.length;
        const newCharactersUsed = chargeTtsCharacters(finalCharCount, systemConfig, setSystemConfig);

        if (newCharactersUsed >= maxFree) {
            toast.error("Limite de caracteres TTS Cloud excedido! A voz econômica local será usada até o próximo ciclo.");
        }

        // Simulate playing audio by using a generic browser voice for demonstration
        // In a real scenario, the Cloud TTS audio would be played.
        const synth = getLocalSpeechSynth();
        if (synth) {
            const utterance = new SpeechSynthesisUtterance(processedText);
            const cloudVoice = getAvailableCloudVoices().find(v => v.voiceURI === effectiveSofiaConfig.cloudTtsVoiceName);
            if (cloudVoice) {
                // Attempt to find a local equivalent for better demo, or just use default
                const browserVoice = localVoices.find(lv => lv.name.includes(cloudVoice.name.split(' ')[0]) && lv.lang === cloudVoice.lang) || getAvailableLocalVoices()[0];
                utterance.voice = browserVoice;
            }
            utterance.lang = 'pt-BR';
            utterance.rate = 1.0;
            utterance.pitch = 1;
            synth.speak(utterance);
        }
    }
};
